import api from "./http";
import { clearMenuCache } from "@/components/menu.vue";
import { clearQuickActionsCache } from "@/components/work-sched/right-sidebar.vue";
import { clearSearchIndexCache } from "@/components/nav-bar.vue";

export async function login({ login, password }) {
  if (!login || !password) {
    throw new Error("Email/Employee No and password are required.");
  }

  try {
    const { data } = await api.post("/auth/login", { login, password });
    // 2FA-enabled accounts get a challenge instead of a token — the caller must
    // collect a code and call verifyTwoFactor(). Don't persist anything yet.
    if (data.status === "2fa_required") {
      return { twoFactorRequired: true, challenge: data.challenge };
    }
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    return { user: data.user };
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data || {};
    let message;
    if (body.errors) {
      message = Object.values(body.errors).flat().join(" ");
    } else if (body.message) {
      message = body.message;
    } else if (status === 401) {
      message = "Invalid email/employee no or password.";
    } else if (status >= 500) {
      message = "Server error. Please try again later.";
    } else if (!err.response) {
      message = "Cannot reach the API server. Check your network or backend.";
    } else {
      message = "Sign in failed.";
    }
    const e = new Error(message);
    e.status = status;
    throw e;
  }
}

// Second step of a 2FA login: exchange the challenge + a TOTP / recovery code
// for the real token. Throws a friendly Error on failure.
export async function verifyTwoFactor({ challenge, code }) {
  try {
    const { data } = await api.post("/auth/2fa/verify", { challenge, code });
    localStorage.setItem("jwt", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    return data.user;
  } catch (err) {
    const status = err.response?.status;
    const body = err.response?.data || {};
    let message = body.message || "Verification failed.";
    if (status === 429) message = "Too many attempts. Please wait a moment and try again.";
    const e = new Error(message);
    e.status = status;
    e.expired = status === 401 && /expired/i.test(body.message || "");
    throw e;
  }
}

// ===== 2FA enrollment / management (authenticated) =====
export async function enableTwoFactor() {
  const { data } = await api.post("/auth/2fa/enable");
  return data; // { secret, otpauth_url, qr_svg }
}

export async function confirmTwoFactor(code) {
  const { data } = await api.post("/auth/2fa/confirm", { code });
  if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
  return data; // { recovery_codes, user }
}

export async function disableTwoFactor(password) {
  const { data } = await api.post("/auth/2fa/disable", { password });
  if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
  return data.user;
}

export async function regenerateRecoveryCodes(password) {
  const { data } = await api.post("/auth/2fa/recovery-codes", { password });
  return data.recovery_codes;
}

// Step-up check used to gate access to sensitive pages. Resolves on a valid
// authenticator code; throws (with err.response.data.code) on an invalid one.
export async function verifyTwoFactorCode(code) {
  const { data } = await api.post("/auth/2fa/verify-code", { two_factor_code: code });
  return data;
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch (e) {
    // swallow: token may already be invalid
  }
  localStorage.removeItem("jwt");
  localStorage.removeItem("user");
  // Drop any 90-day "keep current password" snoozes so the next login re-prompts.
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("worksched.pwdSnooze."))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  clearMenuCache();
  clearQuickActionsCache();
  clearSearchIndexCache();
  return true;
}

export async function getProfile() {
  const { data } = await api.get("/profile");
  return data.user;
}

export async function changePassword({ currentPassword, newPassword, newPasswordConfirmation }) {
  const { data } = await api.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
    new_password_confirmation: newPasswordConfirmation,
  });
  // Refresh the cached user so must_change_password / password_expired flags clear.
  if (data.user) localStorage.setItem("user", JSON.stringify(data.user));
  return data.user;
}
